import * as React from 'react'
import {
  applyResolvedTheme,
  readStoredThemePreference,
  resolveTheme,
  systemPrefersDark,
  writeStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/helpers/theme'

interface ThemeContextValue {
  /** Stored preference (system follows the OS). */
  preference: ThemePreference
  /** Active light/dark after resolving system. */
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

/**
 * Owns class-based dark mode on `<html>`. Defaults to system; persists choice
 * in localStorage. Pair with the FOUC script in `index.html`.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(() =>
    readStoredThemePreference(),
  )
  const [prefersDark, setPrefersDark] = React.useState(() => systemPrefersDark())

  const resolved = resolveTheme(preference, prefersDark)

  React.useEffect(() => {
    applyResolvedTheme(resolved)
  }, [resolved])

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setPrefersDark(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const setPreference = React.useCallback((next: ThemePreference) => {
    writeStoredThemePreference(next)
    setPreferenceState(next)
  }, [])

  const value = React.useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}
